import type {
  IntegrationManifest,
  IntegrationConnectionView,
  ConnectIntegrationRequest,
  OAuthCallbackRequest,
  CrmEnvironment,
  CrmFieldMapRequest,
  CrmSetupView,
  SaveNamedConnectionRequest,
} from '@logspot-platform/platform-types';
import { api } from './api-client';

export type { IntegrationConnectionView };

type SuccessEnvelope<T> = { data: T };

export type IntegrationsHubResponse = {
  integrations: IntegrationManifest[];
  connections: IntegrationConnectionView[];
};

export type SlackChannel = { id: string; name: string; is_private: boolean };

// Org-scoped Integrations hub: the connector catalog plus the org's live
// connections. Mirrors the backend admin endpoints (api/admin/integrations.ts).
export const IntegrationsService = {
  hub: async (): Promise<IntegrationsHubResponse> => {
    const r = await api.get<SuccessEnvelope<IntegrationsHubResponse>>(
      '/admin/integrations',
    );
    return r.data;
  },

  connect: async (
    input: ConnectIntegrationRequest,
  ): Promise<IntegrationConnectionView> => {
    const r = await api.post<
      SuccessEnvelope<{ connection: IntegrationConnectionView }>
    >('/admin/integrations/connect', input);
    return r.data.connection;
  },

  // A one-time CRM install URL; the browser navigates to it.
  crmAuthorizeUrl: async (
    integrationId: string,
    environment?: CrmEnvironment,
    team?: string,
  ): Promise<string> => {
    const params = new URLSearchParams({
      ...(environment && { environment }),
      ...(team && { team }),
    }).toString();
    const query = params ? `?${params}` : '';
    const r = await api.get<SuccessEnvelope<{ url: string }>>(
      `/admin/integrations/${integrationId}/authorize${query}`,
    );
    return r.data.url;
  },

  crmCallback: async (
    integrationId: string,
    input: OAuthCallbackRequest,
  ): Promise<void> => {
    await api.post<SuccessEnvelope<unknown>>(
      `/admin/integrations/${integrationId}/callback`,
      input,
    );
  },

  crmSetup: async (integrationId: string): Promise<CrmSetupView> => {
    const r = await api.get<SuccessEnvelope<{ setup: CrmSetupView }>>(
      `/admin/integrations/${integrationId}/setup`,
    );
    return r.data.setup;
  },

  createCrmFields: async (integrationId: string): Promise<CrmSetupView> => {
    const r = await api.post<SuccessEnvelope<{ setup: CrmSetupView }>>(
      `/admin/integrations/${integrationId}/setup/fields`,
    );
    return r.data.setup;
  },

  saveCrmFieldMap: async (
    integrationId: string,
    fieldMap: CrmFieldMapRequest['field_map'],
  ): Promise<CrmSetupView> => {
    const body: CrmFieldMapRequest = { field_map: fieldMap };
    const r = await api.post<SuccessEnvelope<{ setup: CrmSetupView }>>(
      `/admin/integrations/${integrationId}/setup/field-map`,
      body,
    );
    return r.data.setup;
  },

  disconnect: async (integrationId: string): Promise<void> => {
    await api.post<SuccessEnvelope<Record<string, never>>>(
      '/admin/integrations/disconnect',
      { integration_id: integrationId },
    );
  },

  // One workspace only; `disconnect('slack')` removes every Slack workspace.
  disconnectSlackWorkspace: async (connectionId: string): Promise<void> => {
    await api.delete<SuccessEnvelope<Record<string, never>>>(
      `/admin/integrations/slack/${connectionId}`,
    );
  },

  slackChannels: async (connectionId: string): Promise<SlackChannel[]> => {
    const r = await api.get<SuccessEnvelope<{ channels: SlackChannel[] }>>(
      `/admin/integrations/slack/${connectionId}/channels`,
    );
    return r.data.channels;
  },

  // Named connections: an org saves several webhooks or Clay tables, each by name.
  createNamedConnection: async (
    integrationId: string,
    input: SaveNamedConnectionRequest,
  ): Promise<IntegrationConnectionView> => {
    const r = await api.post<
      SuccessEnvelope<{ connection: IntegrationConnectionView }>
    >(`/admin/integrations/${integrationId}/connections`, input);
    return r.data.connection;
  },

  updateNamedConnection: async (
    integrationId: string,
    connectionId: string,
    input: SaveNamedConnectionRequest,
  ): Promise<IntegrationConnectionView> => {
    const r = await api.patch<
      SuccessEnvelope<{ connection: IntegrationConnectionView }>
    >(
      `/admin/integrations/${integrationId}/connections/${connectionId}`,
      input,
    );
    return r.data.connection;
  },

  removeNamedConnection: async (
    integrationId: string,
    connectionId: string,
  ): Promise<void> => {
    await api.delete<SuccessEnvelope<Record<string, never>>>(
      `/admin/integrations/${integrationId}/connections/${connectionId}`,
    );
  },

  webhookSecret: async (connectionId: string): Promise<string | null> => {
    const r = await api.get<SuccessEnvelope<{ secret: string | null }>>(
      `/admin/integrations/webhook/connections/${connectionId}/secret`,
    );
    return r.data.secret;
  },
};
